import React, { useEffect, useState, useCallback, useRef } from 'react';
import { NewsArticle, NewsTopic } from '../../types';
import { newsService } from '../../services/newsService';
import NewsCard from './NewsCard';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

interface NewsListProps {
  limit?: number;
  topicId?: string;
}

interface CacheData {
  articles: NewsArticle[];
  total: number;
  timestamp: number;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5分钟缓存
const cache = new Map<string, CacheData>();

const NewsList: React.FC<NewsListProps> = ({ limit = 9, topicId }) => {
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [topics, setTopics] = useState<NewsTopic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTopicId, setSelectedTopicId] = useState<string | undefined>(topicId);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const pageSize = limit;
  const loadingRef = useRef(false);

  // 监听外部传入的 topicId 变化
  useEffect(() => {
    setSelectedTopicId(topicId);
    setCurrentPage(1); // 重置页码
  }, [topicId]);

  const getCacheKey = useCallback((page: number, topicId?: string, date?: Date) => {
    const dateStr = date ? date.toISOString().split('T')[0] : 'all';
    return `articles_${page}_${topicId || 'all'}_${dateStr}_${pageSize}`;
  }, [pageSize]);

  const getFromCache = useCallback((page: number, topicId?: string, date?: Date) => {
    const key = getCacheKey(page, topicId, date);
    const cached = cache.get(key);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached;
    }
    return null;
  }, [getCacheKey]);

  const setCache = useCallback((page: number, topicId: string | undefined, data: CacheData, date?: Date) => {
    const key = getCacheKey(page, topicId, date);
    cache.set(key, { ...data, timestamp: Date.now() });
  }, [getCacheKey]);

  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const cached = localStorage.getItem('news_topics');
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < CACHE_DURATION) {
            setTopics(data);
            return;
          }
        }

        const topicsData = await newsService.getTopics();
        setTopics(topicsData);
        localStorage.setItem('news_topics', JSON.stringify({
          data: topicsData,
          timestamp: Date.now()
        }));
      } catch (err) {
        console.error('Failed to fetch topics:', err);
      }
    };

    fetchTopics();
  }, []);

  const fetchArticles = useCallback(async () => {
    if (loadingRef.current) return;
    
    try {
      const skip = (currentPage - 1) * pageSize;
      
      // 检查缓存
      const cached = getFromCache(currentPage, selectedTopicId, selectedDate);
      if (cached) {
        setArticles(cached.articles);
        setTotalItems(cached.total);
        return;
      }

      loadingRef.current = true;
      setLoading(true);
      
      // 添加日期参数
      const date = selectedDate.toISOString().split('T')[0];
      const response = await newsService.getArticles(pageSize, skip, selectedTopicId, date);
      setArticles(response.items);
      setTotalItems(response.total);
      setError(null);

      // 缓存结果
      setCache(currentPage, selectedTopicId, {
        articles: response.items,
        total: response.total,
        timestamp: Date.now()
      }, selectedDate);

    } catch (err) {
      setError('获取新闻失败');
      console.error(err);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [currentPage, selectedTopicId, pageSize, getFromCache, setCache, selectedDate]);

  // 监听日期变化
  useEffect(() => {
    setCurrentPage(1); // 重置页码
    fetchArticles();
  }, [selectedDate, fetchArticles]);

  const handleTopicChange = (topicId: string | undefined) => {
    setSelectedTopicId(topicId);
    setCurrentPage(1);
  };

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date || new Date());
    setCurrentPage(1); // 重置页码
  };

  const totalPages = Math.ceil(totalItems / pageSize);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  // 预加载下一页
  useEffect(() => {
    if (currentPage < totalPages) {
      const nextPage = currentPage + 1;
      const skip = nextPage * pageSize;
      const date = selectedDate.toISOString().split('T')[0];
      newsService.getArticles(pageSize, skip, selectedTopicId, date).then(response => {
        setCache(nextPage, selectedTopicId, {
          articles: response.items,
          total: response.total,
          timestamp: Date.now()
        }, selectedDate);
      });
    }
  }, [currentPage, totalPages, pageSize, selectedTopicId, setCache, selectedDate]);

  if (loading && articles.length === 0) {
    return (
      <div className="text-center py-10">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-10 text-red-500">
        {error}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleTopicChange(undefined)}
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                !selectedTopicId
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              全部
            </button>
            {topics.map((topic) => (
              <button
                key={topic.id}
                onClick={() => handleTopicChange(topic.id)}
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  selectedTopicId === topic.id
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {topic.name}
              </button>
            ))}
          </div>
          
          <div className="flex items-center gap-2">
            <DatePicker
              selected={selectedDate}
              onChange={handleDateChange}
              dateFormat="yyyy-MM-dd"
              isClearable
              placeholderText="选择日期筛选"
              className="px-3 py-1 border border-gray-300 rounded-md text-sm"
              maxDate={new Date()}
            />
            {selectedDate && (
              <button
                onClick={() => handleDateChange(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                清除日期
              </button>
            )}
          </div>
        </div>
      </div>

      <div className={loading ? "opacity-50 transition-opacity duration-200" : "transition-opacity duration-200"}>
        {articles.length === 0 ? (
          <div className="text-center py-10 text-gray-500">暂无相关新闻</div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {articles.map((article) => (
              <NewsCard key={article.id} article={article} />
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-8 flex justify-center">
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                  currentPage === 1 || loading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                上一页
              </button>
              
              {[...Array(totalPages)].map((_, index) => {
                const page = index + 1;
                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    disabled={loading}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      currentPage === page
                        ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
                className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                  currentPage === totalPages || loading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                下一页
              </button>
            </nav>
          </div>
        )}
      </div>

      {loading && articles.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary-500 border-t-transparent"></div>
        </div>
      )}
    </div>
  );
};

export default NewsList;
